import { push } from 'react-router-redux';

import { fetchWithRefresh } from '../fetch';
import { LocalStorage } from '../helpers';
import route from '../helpers/Routes';
import type { IToastMessage } from '../helpers/Toastr';
import { toastr } from '../helpers/Toastr';
import { UNAUTHORIZED_REQUEST } from './FetchAction';
import type { DispatchFunc, GetStateFunc } from './types';

export type FetchAction = (dispatch: any, getState: any) => Promise<any>;

export interface IFetchActionConfig<TPayload> {
	url: string;
	method?: string;
	headers?: Headers;
	body?: string | FormData;
	cache?: 'default' | 'no-store' | 'reload' | 'no-cache' | 'force-cache';
	jsonResponse?: boolean;
	redirectOnNotFound?: boolean;
	redirectOnUnauthorized?: boolean;
	unauthorizedUrl?: string;

	startAction: string;
	startActionPayload?: TPayload;
	successAction?: string;
	errorAction?: string;

	payloadTransformer?: (data: TPayload) => TPayload;
	validationFunc?: (dispatch: DispatchFunc, getState: GetStateFunc) => boolean;

	onSuccess?: (
		data: TPayload,
		dispatch: DispatchFunc,
		getState: GetStateFunc,
		responseStatus: number,
		value: any
	) => void;
	onAfterSuccess?: (
		data: TPayload,
		dispatch: DispatchFunc,
		getState: GetStateFunc,
		responseStatus: number,
		value: any
	) => void;
}

const fillConfigDefaults = <TPayload>(
	config: IFetchActionConfig<TPayload>
): IFetchActionConfig<TPayload> => {
	const newConfig = {
		...config,
	};

	newConfig.successAction =
		config.successAction || config.startAction + '_SUCCESS';
	newConfig.errorAction = config.errorAction || config.startAction + '_ERROR';

	if (config.jsonResponse == null) {
		newConfig.jsonResponse = true;
	}
	if (config.redirectOnUnauthorized == null) {
		newConfig.redirectOnUnauthorized = true;
	}
	if (config.redirectOnNotFound == null) {
		newConfig.redirectOnNotFound = true;
	}
	if (config.unauthorizedUrl == null) {
		newConfig.unauthorizedUrl = LocalStorage.isSingleSignOnUser()
			? route.SSOExpiredSession()
			: route.logout();
	}

	return newConfig;
};

const makeRequest = (config: IFetchActionConfig<any>): RequestInit => {
	let headers = config.headers;

	if (!headers) {
		headers = new Headers();
		headers.append('Accept', 'application/json');
		if (!(config.body instanceof FormData)) {
			headers.append('Content-Type', 'application/json');
		}
	}

	const options: RequestInit = {
		method: config.method || 'GET',
		headers,
		cache: config.cache || 'no-cache',
		credentials: 'include',
		body: config.body,
	};

	return options;
};

const dispatchToast = (title: string, body: string): void => {
	const toastMessage: IToastMessage = {
		title,
		body,
	};
	toastr.error(toastMessage);
};

const reduceArrayToString = (prev: string, curr: string): string => {
	return prev + ', ' + curr;
};

/**
 * @deprecated Use `@reduxjs/toolkit`
 */
export function CreateFetchAction<TPayload>(
	config: IFetchActionConfig<TPayload>
): FetchAction {
	config = fillConfigDefaults<TPayload>(config);
	const options = makeRequest(config);

	const url = config.url;

	return (dispatch: DispatchFunc, getState: GetStateFunc) => {
		if (config.validationFunc && !config.validationFunc(dispatch, getState)) {
			return;
		}

		dispatch({
			type: config.startAction,
			payload: config.startActionPayload,
		});

		const handleSuccess = (
			data: TPayload,
			httpResponse: Response,
			value: any
		): any => {
			if (config.onSuccess) {
				config.onSuccess(data, dispatch, getState, httpResponse.status, value);
			}
			if (config.payloadTransformer) {
				data = config.payloadTransformer(data);
			}
			dispatch({
				type: config.successAction,
				payload: data,
			});
			if (config.onAfterSuccess) {
				config.onAfterSuccess(
					data,
					dispatch,
					getState,
					httpResponse.status,
					value
				);
			}
			return {
				value: data,
				httpResponse,
			};
		};

		const handleErrorStateOrThrow = (
			value: any,
			httpResponse: Response
		): any => {
			let errorsPayload = null;
			if (value && value.errors) {
				errorsPayload = value.errors.reduce(reduceArrayToString);
			}

			if (httpResponse.status === 400) {
				if (typeof value === 'string') {
					dispatch({
						type: config.errorAction,
						payload: value,
					});
				} else {
					if (value.statusCode >= 200 && value.statusCode <= 499) {
						if (value.statusCode === 201) {
							dispatch(push('/customers'));
						} else {
							dispatchToast('Validation Error', errorsPayload);
							dispatch({ type: config.errorAction, payload: errorsPayload });
						}
					} else {
						dispatch({ type: config.errorAction, payload: errorsPayload });
					}
				}
			} else if (httpResponse.status === 409) {
				dispatchToast('An Error Has Occurred', errorsPayload);
				dispatch({ type: config.errorAction, payload: errorsPayload });
			} else if (
				(httpResponse.status === 401 || httpResponse.status === 403) &&
				config.redirectOnUnauthorized
			) {
				dispatch({
					type: UNAUTHORIZED_REQUEST,
					payload: true,
				});
				window.location.pathname = config.unauthorizedUrl;
			} else if (httpResponse.status === 404 && config.redirectOnNotFound) {
				if (window.history) {
					window.history.replaceState({}, 'Not Found', '/notfound');
					window.history.go(0);
				} else {
					window.location.pathname = '/notfound';
				}
			} else {
				if (httpResponse.status === 500) {
					dispatchToast('Error', 'Something went wrong with your request.');
				}
				return new Promise((resolve, reject) => {
					reject(value);
				});
			}
			return {
				value,
				httpResponse,
			};
		};

		return fetchWithRefresh(url, options)
			.then((response) => {
				const resClone = response.clone();
				const promise =
					config.jsonResponse || !response.ok
						? response.json().catch(() => resClone.text())
						: response.text();

				return promise.then((value) => {
					if (response.ok) {
						if (
							config.jsonResponse &&
							(response.status === 200 || response.status === 201)
						) {
							return handleSuccess(value.value, response, value);
						} else if (response.status === 200) {
							return handleSuccess(value, response, value);
						} else {
							return handleSuccess(null, response, value);
						}
					} else {
						return handleErrorStateOrThrow(value, response);
					}
				});
			})
			.catch(() => {
				dispatch({ type: config.errorAction });
			});
	};
}
